/**
 * @fileoverview SAP Integration page component
 * @description Main page for viewing SAP synchronized data: movements, invoices, production orders
 */
import React, { useRef, useState, useCallback } from "react";
import { DmsPage, DmsTabber, DmsAltBox, intl } from "dmsbasereact";
import { Typography } from "@mui/material";
import { SapMovementTable } from "../../components/SapMovementTable";
import { InvoiceDocumentTable } from "../../components/InvoiceDocumentTable/InvoiceDocumentTable";
import { ProductionOrderTable } from "../../components/ProductionOrderTable";
import { ProductionOrderTargetTable } from "../../components/ProductionOrderTargetTable";
import { InvoiceDocumentDetailModal } from "../../components/InvoiceDocumentTable/InvoiceDocumentDetailModal";
import { InvoiceCancelModal } from "../../components/InvoiceDocumentTable/InvoiceCancelModal";
import { SapMovementDetailModal } from "../../components/SapMovementTable/SapMovementDetailModal";
import { ProductionOrderDetailModal } from "../../components/ProductionOrderTable/ProductionOrderDetailModal";
import { ProductionOrderTargetDetailModal } from "../../components/ProductionOrderTargetTable/ProductionOrderTargetDetailModal";
import { SAPCommunicationTable } from "../SAPCommunicationPage/components/SAPCommunicationTable";

/**
 * SAP Integration page component
 * @returns {JSX.Element} The page component
 */
export const SapIntegrationPage = () => {
  const movementTableRef = useRef(null);
  const invoiceTableRef = useRef(null);
  const productionOrderTableRef = useRef(null);
  const productionOrderTargetTableRef = useRef(null);

  // Modal state management
  const [movementDetailModalOpen, setMovementDetailModalOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  
  const [invoiceDetailModalOpen, setInvoiceDetailModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [invoiceCancelModalOpen, setInvoiceCancelModalOpen] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);
  const [invoiceTableKey, setInvoiceTableKey] = useState(0);

  const [productionOrderDetailModalOpen, setProductionOrderDetailModalOpen] = useState(false);
  const [selectedProductionOrder, setSelectedProductionOrder] = useState(null);
  
  const [targetDetailModalOpen, setTargetDetailModalOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);

  // Handlers for SAP Movements
  const handleViewMovementDetails = useCallback((row) => {
    setSelectedMovement(row);
    setMovementDetailModalOpen(true);
  }, []);

  const handleCloseMovementDetailModal = useCallback(() => {
    setMovementDetailModalOpen(false);
    setSelectedMovement(null);
  }, []);

  // Handlers for Invoice Documents
  const handleViewInvoiceDetails = useCallback((row) => {
    setSelectedInvoice(row);
    setInvoiceDetailModalOpen(true);
  }, []);

  const handleCloseInvoiceDetailModal = useCallback(() => {
    setInvoiceDetailModalOpen(false);
    setSelectedInvoice(null);
  }, []);

  const handleCancelInvoice = useCallback((row) => {
    setInvoiceToCancel(row);
    setInvoiceCancelModalOpen(true);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setInvoiceCancelModalOpen(false);
    setInvoiceToCancel(null);
  }, []);

  const handleCancelSuccess = useCallback(() => {
    setInvoiceTableKey(prev => prev + 1);
  }, []);

  // Handlers for Production Orders
  const handleViewProductionOrderDetails = useCallback((row) => {
    setSelectedProductionOrder(row);
    setProductionOrderDetailModalOpen(true);
  }, []);

  const handleCloseProductionOrderDetailModal = useCallback(() => {
    setProductionOrderDetailModalOpen(false);
    setSelectedProductionOrder(null);
  }, []);

  // Handlers for Production Order Targets
  const handleViewTargetDetails = useCallback((row) => {
    setSelectedTarget(row);
    setTargetDetailModalOpen(true);
  }, []);

  const handleCloseTargetDetailModal = useCallback(() => {
    setTargetDetailModalOpen(false);
    setSelectedTarget(null);
  }, []);

  const tabs = [
    {
      title: intl.get("Movements").d("Movements"),
      component: (
        <SapMovementTable
          ref={movementTableRef}
          onViewDetails={handleViewMovementDetails}
        />
      ),
    },
    {
      title: intl.get("InvoiceDocuments").d("Invoice Documents"),
      component: (
        <InvoiceDocumentTable
          key={invoiceTableKey}
          ref={invoiceTableRef}
          onViewDetails={handleViewInvoiceDetails}
          onCancel={handleCancelInvoice}
        />
      ),
    },
    {
      title: intl.get("ProductionOrders").d("Production Orders"),
      component: (
        <ProductionOrderTable
          ref={productionOrderTableRef}
          onViewDetails={handleViewProductionOrderDetails}
        />
      ),
    },
    {
      title: intl.get("ProductionOrderTargets").d("Production Order Targets"),
      component: (
        <ProductionOrderTargetTable
          ref={productionOrderTargetTableRef}
          onViewDetails={handleViewTargetDetails}
        />
      ),
    },
    {
      title: intl.get("SAPCommunicationLogs").d("SAP Communication Logs"),
      component: <SAPCommunicationTable />,
    },
  ];

  return (
    <>
      <DmsPage name={intl.get("Traceability").d("Traceability")} manual_slug="sap-integration">
        <DmsAltBox.Card>
          <DmsAltBox.Header>
            <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
              {intl.get("Traceability").d("Traceability")}
            </Typography>
          </DmsAltBox.Header>
          <DmsAltBox.Body>
            <DmsTabber tabs={tabs} pageName={intl.get("Traceability").d("Traceability")} />
          </DmsAltBox.Body>
        </DmsAltBox.Card>
      </DmsPage>

      {/* SAP Movement Detail Modal */}
      <SapMovementDetailModal
        open={movementDetailModalOpen}
        onClose={handleCloseMovementDetailModal}
        movement={selectedMovement}
      />

      {/* Invoice Document Detail Modal */}
      <InvoiceDocumentDetailModal
        open={invoiceDetailModalOpen}
        onClose={handleCloseInvoiceDetailModal}
        invoice={selectedInvoice}
      />

      {/* Invoice Cancel Modal */}
      <InvoiceCancelModal
        open={invoiceCancelModalOpen}
        onClose={handleCloseCancelModal}
        invoice={invoiceToCancel}
        onSuccess={handleCancelSuccess}
      />

      {/* Production Order Detail Modal */}
      <ProductionOrderDetailModal
        open={productionOrderDetailModalOpen}
        onClose={handleCloseProductionOrderDetailModal}
        productionOrder={selectedProductionOrder}
      />

      {/* Production Order Target Detail Modal */}
      <ProductionOrderTargetDetailModal
        open={targetDetailModalOpen}
        onClose={handleCloseTargetDetailModal}
        target={selectedTarget}
      />
    </>
  );
};


